/**
 * components/inspection/stageRef.js — ШАТНЫ ЛАВЛАХ, НЭГ ЭХ СУРВАЛЖ.
 *
 * ЯАГААД: шатны нэршил/өнгө нь frontend-ийн 5 файлд ТУС ТУСДАА бичигдсэн
 * байв («Байнгын», «Мониторинг», «Байнгын хяналт (мониторинг)» — гурван
 * өөр нэр НЭГ шатыг заана). Лавлах дээр нэрийг зассан ч тэдгээр газар
 * хуучнаараа гарч, хэрэглэгч «би зассан шүү дээ» гэж эргэлздэг байв.
 *
 * Энэ модуль нь `GET /xab/inspection-stages`-ыг НЭГ УДАА татаж кэшлээд,
 * бүх газарт ижил нэр/өнгө/icon өгнө. Кэш бэлэн болоогүй эсвэл сүлжээ
 * унасан бол доорх нөөц зураглалаар харуулна — хоосон зай харуулахаас
 * хамаагүй дээр.
 *
 * ⚠ НӨӨЦ нь ЗӨВХӨН нөөц: лавлахад утга байвал ҮРГЭЛЖ тэр нь дээгүүрт.
 *   Нөөцийг «ингэж байх ёстой» гэж ойлгож засвал дахин хоёр эх сурвалж
 *   болно.
 */
import { useEffect, useState } from "react";

import { xabInspectionApi } from "src/api/xabApi";

/** Сүлжээгүй / лавлах бэлэн биш үеийн нөөц (журмын 7 шат) */
export const STAGE_FALLBACK = {
  I: { label: "I шат", color: "#0EA5E9", icon: "fa-user-check" },
  II: { label: "II шат", color: "#22C55E", icon: "fa-users" },
  III: { label: "III шат", color: "#F59E0B", icon: "fa-users-cog" },
  IV: { label: "IV шат", color: "#A32D2D", icon: "fa-clipboard-check" },
  targeted: { label: "Зорилтот", color: "#EA580C", icon: "fa-crosshairs" },
  monitoring: { label: "Байнгын", color: "#6366F1", icon: "fa-eye" },
  irregular: { label: "Ээлжит бус", color: "#CA8A04", icon: "fa-bolt" },
};

/** Журмаар тогтсон дараалал — лавлах татагдаагүй үед эрэмбэлэхэд */
export const STAGE_ORDER = [
  "I", "II", "III", "IV", "targeted", "monitoring", "irregular",
];

let _rows = null; // татагдсан мөрүүд
let _promise = null; // зэрэг дуудалтууд нэг хүсэлт хуваалцана
const _subs = new Set(); // кэш бэлэн болоход дуудагдах hook-ууд

function _index() {
  const out = {};
  (_rows || []).forEach((s) => {
    out[s.code] = {
      label: s.short_name || s.name || s.code,
      full: s.name || s.short_name || s.code,
      color: s.color_hex || STAGE_FALLBACK[s.code]?.color || "#64748B",
      icon: s.icon || STAGE_FALLBACK[s.code]?.icon || "",
      frequency: s.frequency_label || "",
      actor: s.actor_label || "",
      order: s.order_no ?? 99,
    };
  });
  return out;
}

let _map = {};

/** Лавлахыг татна (нэг л удаа; `force` — засвар хийсний дараа). */
export function loadStages(force = false) {
  if (_rows && !force) return Promise.resolve(_rows);
  if (_promise && !force) return _promise;
  _promise = xabInspectionApi
    .getStages()
    .then((d) => {
      _rows = Array.isArray(d) ? d : [];
      _map = _index();
      _subs.forEach((fn) => {
        try {
          fn(_rows);
        } catch (e) {
          /* subscriber-ийн алдаа бусдыг тасалдуулахгүй */
        }
      });
      return _rows;
    })
    .catch(() => {
      _rows = _rows || [];
      return _rows;
    })
    .finally(() => {
      _promise = null;
    });
  return _promise;
}

const _fb = (code) => STAGE_FALLBACK[code] || null;

/** Шатны товч нэр — лавлахаас, байхгүй бол нөөцөөс, эс бөгөөс кодоороо. */
export const stageLabel = (code) =>
  _map[code]?.label || _fb(code)?.label || (code ? String(code) : "");
/** Бүтэн нэр (жагсаалтын гарчиг, тайлбарт) */
export const stageFullLabel = (code) =>
  _map[code]?.full || _fb(code)?.label || (code ? String(code) : "");
export const stageColor = (code) =>
  _map[code]?.color || _fb(code)?.color || "#64748B";
export const stageIcon = (code) => _map[code]?.icon || _fb(code)?.icon || "";
export const stageFrequency = (code) => _map[code]?.frequency || "";
export const stageActor = (code) => _map[code]?.actor || "";

/**
 * Hook — лавлах татагдмагц дахин render болно.
 *
 * Буцаана: { rows, ready, label, full, color, icon, options }
 *   `options` — Select/товчны сонголт (лавлахын дараалалаар).
 */
export function useStageRef() {
  const [rows, setRows] = useState(_rows);
  useEffect(() => {
    const fn = (r) => setRows([...(r || [])]);
    _subs.add(fn);
    loadStages().then((r) => {
      if (!_rows || r !== rows) fn(r);
    });
    return () => _subs.delete(fn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const codes = (rows || []).length
    ? (rows || []).map((s) => s.code)
    : STAGE_ORDER;

  return {
    rows: rows || [],
    ready: !!(rows || []).length,
    label: stageLabel,
    full: stageFullLabel,
    color: stageColor,
    icon: stageIcon,
    frequency: stageFrequency,
    actor: stageActor,
    codes,
    options: codes.map((c) => ({
      value: c,
      label: stageLabel(c),
      color: stageColor(c),
      icon: stageIcon(c),
      hint: stageFrequency(c),
      actor: stageActor(c),
    })),
  };
}

// Аппын эхэнд нэг удаа — дэлгэц гарахад нэр нь аль хэдийн бэлэн байна
loadStages();
